import { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { FormData, PermissionGroupItem } from '../types';

type UsePermissionGroupParams = {
  items: Array<PermissionGroupItem>;
  allItems: Array<PermissionGroupItem>;
  defaultExpanded?: boolean;
  form: UseFormReturn<FormData>;
  userPermissions?: Array<string>;
  readOnly?: boolean;
};

export const usePermissionGroup = ({
  items,
  allItems,
  defaultExpanded = true,
  form,
  userPermissions,
  readOnly,
}: UsePermissionGroupParams) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const allSelectedPermissions = form.watch('permissions');
  const userPermissionsSet = new Set(userPermissions);

  const isDisabled = (item: PermissionGroupItem) =>
    item.permissions.length === 0 || !isRequirementMet(item);

  const isRestrictedByUserPermissions = (item: PermissionGroupItem) =>
    userPermissions !== undefined &&
    item.permissions.some((permission) => !userPermissionsSet.has(permission));

  const isItemChecked = (item: PermissionGroupItem) => {
    return item.permissions.every((permission) =>
      allSelectedPermissions.includes(permission),
    );
  };

  const getDependentItems = (item: PermissionGroupItem) =>
    item.id ? items.filter((i) => i.requiresId === item.id) : [];

  const getRequiredItem = (item: PermissionGroupItem) =>
    item.requiresId ? items.find((i) => i.id === item.requiresId) : undefined;

  const isRequirementMet = (item: PermissionGroupItem) => {
    const required = getRequiredItem(item);
    if (!required) return true;
    return required.permissions.every((permission) =>
      allSelectedPermissions.includes(permission),
    );
  };

  const onChange = (item: PermissionGroupItem) => () => {
    if (readOnly || isDisabled(item) || isRestrictedByUserPermissions(item)) {
      return;
    }

    const isCurrentlyChecked = item.permissions.every((permission) =>
      allSelectedPermissions.includes(permission),
    );

    if (isCurrentlyChecked) {
      // When unchecking, also remove permissions of dependent items
      const permissionsToRemove = new Set([
        ...item.permissions,
        ...getDependentItems(item).flatMap((dep) => dep.permissions),
      ]);
      // Keep permissions that are still needed by another checked item in any group
      const permissionsToKeep = new Set(
        allItems
          .filter(
            (other) =>
              !other.permissions.every((p) => permissionsToRemove.has(p)) &&
              other.permissions.every((p) =>
                allSelectedPermissions.includes(p),
              ),
          )
          .flatMap((other) => other.permissions),
      );
      form.setValue(
        'permissions',
        allSelectedPermissions.filter(
          (p) => !permissionsToRemove.has(p) || permissionsToKeep.has(p),
        ),
      );
    } else {
      form.setValue('permissions', [
        ...new Set([...allSelectedPermissions, ...item.permissions]),
      ]);
    }
  };

  return {
    expanded,
    setExpanded,
    isDisabled,
    isRestrictedByUserPermissions,
    isItemChecked,
    getRequiredItem,
    isRequirementMet,
    onChange,
  };
};
